import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function MarkerCollectionSpotlight() {
  const [art, setArt] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    http.get("/marker-collection/public/spotlight").then(({ data }) => {
      if (data && data.id) setArt(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, { threshold: 0.15 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [art]);

  if (!art) return null;

  const img = art.main_image ? resolveImage(art.main_image) : null;
  const isSold = art.original_status === "Sold";

  return (
    <section
      ref={ref}
      data-testid="mc-spotlight"
      className="relative overflow-hidden border-y border-[#C9A055]/20"
      style={{ background: "linear-gradient(135deg, #0a0806 0%, #111 40%, #0d0b09 100%)" }}
    >
      {/* Animated grain texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundSize: "128px"}} />

      {/* Gold line accent */}
      <div className={`absolute top-0 left-0 right-0 h-[1px] transition-all duration-[2000ms] ease-out ${visible ? "opacity-100" : "opacity-0"}`}
        style={{ background: "linear-gradient(90deg, transparent 0%, #C9A055 50%, transparent 100%)" }} />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center relative z-10">
        {/* Image Side */}
        <div className={`relative transition-all duration-[1200ms] ease-out ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
          {/* Floating frame effect */}
          <div className="relative max-w-[480px] mx-auto lg:mx-0">
            {/* Shadow glow */}
            <div className="absolute -inset-4 rounded-sm opacity-20 blur-2xl" style={{ background: "radial-gradient(ellipse, #C9A055 0%, transparent 70%)" }} />

            {/* Frame border */}
            <div className="relative border border-[#C9A055]/30 p-2 bg-black/50 backdrop-blur-sm">
              <div className="relative aspect-[4/5] overflow-hidden bg-[#0a0a0a]">
                {img ? (
                  <img
                    src={img}
                    alt={art.title}
                    className={`w-full h-full object-cover transition-all duration-[2000ms] ease-out ${visible ? "scale-100" : "scale-110"}`}
                    data-testid="mc-spotlight-image"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="font-ui text-[10px] tracking-[0.3em] text-[#C9A055]/40 uppercase">Marker Collection</span>
                  </div>
                )}
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

                {/* Type badge */}
                {art.art_type && (
                  <div className={`absolute top-4 left-4 px-3 py-1.5 text-[9px] tracking-[0.25em] uppercase font-bold transition-all duration-700 delay-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}
                    style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", color: "#C9A055", border: "1px solid rgba(201,160,85,0.2)" }}>
                    {isSold ? "SOLD" : art.art_type}
                  </div>
                )}
              </div>
            </div>

            {/* Corner marks */}
            <div className="absolute -top-1 -left-1 w-4 h-4 border-t border-l border-[#C9A055]/50" />
            <div className="absolute -top-1 -right-1 w-4 h-4 border-t border-r border-[#C9A055]/50" />
            <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b border-l border-[#C9A055]/50" />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b border-r border-[#C9A055]/50" />
          </div>
        </div>

        {/* Text Side */}
        <div className="lg:pl-4">
          <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="font-ui text-[10px] tracking-[0.4em] text-[#C9A055]/70 uppercase mb-6">
              From the Marker Collection
            </div>
          </div>

          <div className={`transition-all duration-700 delay-400 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-[#F4F1EA] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700 }}>
              {art.title}
            </h2>
          </div>

          <div className={`transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] tracking-[0.25em] uppercase text-[#C9A055] mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Made by Hand. Mark by Mark.
            </p>
          </div>

          {art.description && (
            <div className={`transition-all duration-700 delay-[600ms] ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="text-[#B5B0A7] text-base leading-relaxed mb-8 max-w-md"
                style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {art.description.length > 180 ? art.description.slice(0, 180) + "..." : art.description}
              </p>
            </div>
          )}

          {/* Meta details */}
          <div className={`flex flex-wrap gap-x-8 gap-y-3 mb-10 transition-all duration-700 delay-[700ms] ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {art.medium && (
              <div>
                <div className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] uppercase mb-1">Medium</div>
                <div className="text-[#F4F1EA] text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{art.medium}</div>
              </div>
            )}
            {art.dimensions && (
              <div>
                <div className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] uppercase mb-1">Size</div>
                <div className="text-[#F4F1EA] text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{art.dimensions}</div>
              </div>
            )}
            {art.price && !isSold && (
              <div>
                <div className="font-ui text-[8px] tracking-[0.3em] text-[#8a857d] uppercase mb-1">Price</div>
                <div className="text-[#C9A055] text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ${Number(art.price).toFixed(2)}
                </div>
              </div>
            )}
          </div>

          {/* CTA buttons */}
          <div className={`flex flex-wrap gap-4 transition-all duration-700 delay-[800ms] ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Link
              to={`/marker-collection/${art.slug}`}
              className="group inline-flex items-center gap-3 bg-[#C9A055] text-black px-8 py-4 text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-[#E0BC72] transition-colors"
              data-testid="mc-spotlight-view"
            >
              {isSold ? "View Artwork" : "View This Piece"}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/marker-collection"
              className="inline-flex items-center gap-2 border border-[#C9A055]/40 text-[#C9A055] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C9A055]/10 transition-colors"
              data-testid="mc-spotlight-collection"
            >
              Browse Collection
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom gold line */}
      <div className={`absolute bottom-0 left-0 right-0 h-[1px] transition-all duration-[2000ms] ease-out ${visible ? "opacity-100" : "opacity-0"}`}
        style={{ background: "linear-gradient(90deg, transparent 0%, #C9A055 50%, transparent 100%)" }} />
    </section>
  );
}
